
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ActiveTrainee } from "@/types/coach";

export const useActiveTrainees = () => {
  const [activeTrainees, setActiveTrainees] = useState<ActiveTrainee[]>([]);
  const { toast } = useToast();

  const fetchActiveTrainees = useCallback(async (coachId: string) => {
    try {
      const { data: permissions, error: permissionsError } = await supabase
        .from("data_sharing_permissions")
        .select("*")
        .eq("coach_id", coachId)
        .eq("is_active", true);

      if (permissionsError) {
        throw permissionsError;
      }

      if (!permissions || permissions.length === 0) {
        setActiveTrainees([]);
        return;
      }

      const traineesWithProfiles = await Promise.all(
        permissions.map(async (permission) => {
          try {
            const { data: profile, error: profileError } = await supabase
              .rpc("get_trainee_safe_profile", { trainee_id: permission.trainee_user_id });

            if (profileError) {
              console.error("Erreur profil stagiaire:", permission.trainee_user_id, profileError);
            }

            const safeProfile = profile?.[0] || null;
            return {
              ...permission,
              profiles: safeProfile ? {
                username: safeProfile.username,
                avatar_url: safeProfile.avatar_url,
                created_at: safeProfile.created_at
              } : { username: "Stagiaire inconnu" }
            };
          } catch (err) {
            console.error("Erreur traitement stagiaire:", err);
            return {
              ...permission,
              profiles: { username: "Stagiaire inconnu" }
            };
          }
        })
      );

      const uniqueTrainees = traineesWithProfiles.reduce((acc, permission) => {
        const existing = acc.find(t => t.trainee_user_id === permission.trainee_user_id);
        if (!existing) {
          acc.push(permission);
        }
        return acc;
      }, [] as any[]);

      setActiveTrainees(uniqueTrainees);

    } catch (error: any) {
      console.error("Erreur fetchActiveTrainees:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les stagiaires supervisés",
      });
    }
  }, [toast]);

  return {
    activeTrainees,
    fetchActiveTrainees,
  };
};
